import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { SchemaManualOffer } from 'cpool-schemas';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { setQueryPosition } from '~/store/queryPositionStore.ts';
import { appendPositionToQueryKey } from '~/utils/appendPositionToQueryKey.ts';
import { queryKeys } from '~/utils/queryKeys.ts';

type Props = {
    aggregateId: string;
    id: string;
};

type mutationProps = {
    formData: SchemaManualOffer;
};

export const useUpdateOffer = ({ aggregateId, id }: Props) => {
    const search = useSearch({ strict: false });
    const offerFilter = 'offerFilter' in search ? search.offerFilter : {};
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const queryKey = queryKeys.getAcceptedOffers(aggregateId, offerFilter);

    return useMutation({
        mutationFn: async ({ formData }: mutationProps) => {
            return APICommunicator.sendCommand({
                aggregateId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.changeManualOffer,
                payload: formData,
            });
        },
        onError: () => {
            toast.error(t('OFFER_MANAGEMENT.OFFER_CHANGED_ERROR'));
        },
        onMutate: ({ formData }) => {
            queryClient.setQueryData(
                appendPositionToQueryKey.current(queryKey),
                (currentData: SchemaLoanRequestResolver_GetAcceptedOffers) => {
                    if (Array.isArray(currentData)) {
                        const offer = currentData.find((entry) => entry.id === id);
                        if (offer) {
                            // Update the properties of the offer object
                            Object.assign(offer, formData);
                        }
                    }
                    return currentData;
                },
            );
        },
        onSettled: (data) => {
            queryClient.invalidateQueries({ queryKey: appendPositionToQueryKey.custom(queryKey, data?.position) });
        },
        onSuccess: (data) => {
            setQueryPosition(JSON.stringify(queryKey), data.position);
        },
    });
};
